"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
const node_fetch_1 = __importDefault(require("node-fetch"));
const FormData = require("form-data");
const qs = require("qs");
const fs = require("../fs");
const packageJson = require("../package.json");
class Agent {
    constructor(baseUrl, httpAgent) {
        this.setBaseUrl(baseUrl);
        this.httpAgent = httpAgent;
    }
    setBaseUrl(baseUrl) {
        this.baseUrl = baseUrl;
    }
    get(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'get' }));
    }
    head(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'head' }));
    }
    post(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'post' }));
    }
    put(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'put' }));
    }
    patch(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'patch' }));
    }
    delete(options) {
        return this.request(Object.assign(Object.assign({}, options), { method: 'delete' }));
    }
    request({ uri, method, headers = undefined, data = undefined, auth, query = undefined, form = undefined, files = undefined, context = undefined, isBuffer = false }) {
        const requestFiles = this._sanitizeFiles(files);
        const requestParams = this._buildRequest({ uri, method, headers, data, auth, query, form, context, files: requestFiles });
        return this._promiseResponse(requestParams, isBuffer);
    }
    _promiseResponse(requestParams, isBuffer, makerequest = node_fetch_1.default) {
        let status;
        return makerequest(requestParams[0], requestParams[1])
            .then((resp) => {
            status = resp.status;
            if (!resp.ok) {
                return resp.text().then((err) => {
                    const objError = JSON.parse(err);
                    const response = Object.assign(resp, { text: err });
                    throw Object.assign(objError, { response });
                });
            }
            if (status === 204) {
                return { body: {}, statusCode: 204 };
            }
            if (isBuffer) {
                return resp.blob().then((blob) => blob.arrayBuffer().then((arrayBuffer) => {
                    if (!this.isForBrowser()) {
                        return Buffer.from(arrayBuffer);
                    }
                    return arrayBuffer;
                }));
            }
            return resp.json().then((body) => ({
                body: body,
                statusCode: status
            }));
        }).catch((error) => {
            const errorType = status ? `HTTP error ${status}` : 'Network error';
            let errorDescription = `${errorType} from ${requestParams[0]}`;
            let shortErrorDescription;
            if (error.error_description) {
                errorDescription = `${errorDescription} - ${error.error_description}`;
                shortErrorDescription = error.error_description;
            }
            const reason = new Error(errorDescription);
            Object.assign(reason, {
                statusCode: status,
                errorDescription,
                shortErrorDescription,
                error,
                body: error
            });
            throw reason;
        });
    }
    _buildRequest({ uri, method, headers, data, auth, query, form, files, context }) {
        let actualUri = uri;
        if (this.baseUrl && uri[0] === '/') {
            actualUri = `${this.baseUrl}${uri}`;
        }
        if (query) {
            const queryParams = qs.stringify(query);
            if (queryParams) {
                const hasParams = actualUri.includes('?');
                actualUri = `${actualUri}${hasParams ? '&' : '?'}${queryParams}`;
            }
        }
        const pkg = packageJson;
        const userAgentHeader = { 'User-Agent': `${pkg.name}/${pkg.version} (${pkg.repository.url})` };
        let body;
        let contentTypeHeader;
        if (files) {
            contentTypeHeader = {};
            body = this._getFromData(files, form);
        }
        else if (form) {
            contentTypeHeader = { 'Content-Type': 'application/x-www-form-urlencoded' };
            body = qs.stringify(form);
        }
        else if (data) {
            if (data instanceof FormData) {
                body = data;
            }
            else {
                contentTypeHeader = { 'Content-Type': 'application/json' };
                body = JSON.stringify(data);
            }
        }
        const finalHeaders = Object.assign({}, userAgentHeader, contentTypeHeader, this._getAuthorizationHeader(auth), this._getContextHeaders(context), headers);
        const init = { method, body, headers: finalHeaders };
        if (this.httpAgent) {
            init.agent = this.httpAgent;
        }
        return [actualUri, init];
    }
    isForBrowser() {
        return typeof window !== 'undefined';
    }
    _getFromData(files, form) {
        const formData = new FormData();
        for (const [name, file] of Object.entries(files)) {
            let path = file.path;
            let fileData = file.data;
            if (!this.isForBrowser()) {
                const nodeFormData = this._getNodeFormData(file);
                path = nodeFormData.path;
                fileData = nodeFormData.file;
            }
            formData.append(name, fileData, path);
        }
        if (form) {
            for (const [name, value] of Object.entries(form)) {
                if (value !== undefined) {
                    formData.append(name, String(value));
                }
            }
        }
        return formData;
    }
    _getNodeFormData(file) {
        let fileData = file.data;
        if (typeof file.data === 'string') {
            fileData = fs.createReadStream(file.data);
        }
        return {
            file: fileData,
            path: { filepath: file.path }
        };
    }
    _getContextHeaders(context = {}) {
        return Object.assign({}, this._getToolContext(context.tool), this._getProjectContext(context.project));
    }
    _getToolContext(tool = { name: '' }) {
        let value = '';
        if (tool.name) {
            value += this._toolIdent(tool);
            if (tool.components) {
                for (const component of tool.components) {
                    value += ', ' + this._toolIdent(component);
                }
            }
        }
        if (value) {
            return { 'X-Particle-Tool': value };
        }
        return {};
    }
    _toolIdent(tool) {
        return this._nameAtVersion(tool.name, tool.version);
    }
    _nameAtVersion(name, version) {
        let value = '';
        if (name) {
            value += name;
            if (version !== undefined) {
                value += '@' + version;
            }
        }
        return value;
    }
    _getProjectContext(project = { name: '' }) {
        const value = this._buildSemicolonSeparatedProperties(project, 'name');
        if (value) {
            return { 'X-Particle-Project': value };
        }
        return {};
    }
    _buildSemicolonSeparatedProperties(obj, primaryProperty = '') {
        let value = '';
        if (obj[primaryProperty]) {
            value += obj[primaryProperty];
            for (const prop in obj) {
                if (prop !== primaryProperty && Object.hasOwnProperty.call(obj, prop)) {
                    value += '; ' + prop + '=' + obj[prop];
                }
            }
        }
        return value;
    }
    _getAuthorizationHeader(auth) {
        if (typeof auth === 'string') {
            return { Authorization: `Bearer ${auth}` };
        }
        return {};
    }
    _sanitizeFiles(files) {
        let requestFiles;
        if (files) {
            requestFiles = {};
            Object.keys(files).forEach((k, i) => {
                const name = i ? `file${i + 1}` : 'file';
                requestFiles[name] = {
                    data: files[k],
                    path: k
                };
            });
        }
        return requestFiles;
    }
}
module.exports = Agent;
//# sourceMappingURL=Agent.js.map